import React from "react";
import moment from "moment";
import {
    request,
    requestWithHeaders,
    requestWithParams,
} from "../beta/utils/fetchData";
import { useEffect, useState } from "react";
import clsx from "clsx";
import Cookies from "js-cookie";
import { showInfoNotification } from "../beta/utils/toastrNotifications";
import { ViewportContext } from "../beta/utils/hooks/viewportProvider";
import getViewSize from "../beta/utils/breakpointsUtils";
import useResizeObserver from "../beta/utils/hooks/useResizeObserver";
import Header from "./Header";
import Footer from "./Footer";
import { MenuItem, FormControl, InputLabel } from "@mui/material";
import { MuiThemeProvider } from "@material-ui/core/styles";
import { StylesProvider, jssPreset } from "@material-ui/styles";
import theme from "../beta/config/theme";
import { create } from "jss";
import Loading from "../beta/components/helpers/Loading";
const jss = create({
    ...jssPreset(),
    insertionPoint: document.getElementById("JssInsertionPoint"),
});

import Select from "components/inputs/OutlinedSelect";
import { usePasswordExpirationNotification } from "./hooks/usePasswordExpirationNotification";
import { useCallback } from "react";

const Landing = () => {
    const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");

    const [products, setProducts] = React.useState([]);

    const [user, setUser] = React.useState(null);

    const useThemeClasses = (isPrintMode = false) => {
        if (isPrintMode) return "theme-light";

        const { darkMode } = user || {};
        localStorage.setItem("color-theme", darkMode ? "dark" : "light");
        return darkMode ? "theme-dark" : "theme-light";
    };

    const [classes, setClasses] = React.useState(
        clsx(`view-${currentBreakpoint}`, useThemeClasses())
    );

    useEffect(() => {
        setClasses(clsx(`view-${currentBreakpoint}`, useThemeClasses()));
    }, [user, currentBreakpoint]);

    // Handle password expiration notification
    usePasswordExpirationNotification(user);

    useEffect(() => {
        const refreshToken = async () => {
            try {
                let resp = await fetch("/api/authentication/refresh-token", {
                    method: "POST",
                });
            } catch (e) {
                console.log(e);
            }
        };
        const fetchData = async () => {
            try {
                let token = Cookies.get("_t");
                const headers = token
                    ? { Authorization: `bearer ${token}` }
                    : {};
                const response = await requestWithHeaders(
                    "/home/getsubscribedproducts",
                    "GET",
                    null,
                    headers
                );
                setProducts(response.data);
                // Initialize dropdown values with smart defaults
                const defaults = {};
                response.data.forEach((product) => {
                    if (
                        product.productConfig &&
                        product.productConfig.length > 0
                    ) {
                        // Prefer current user
                        const currentUserConfig = product.productConfig.find(
                            (opt) => opt.isCurrentUser
                        );
                        if (currentUserConfig) {
                            defaults[product.productName] =
                                currentUserConfig.username;
                        } else {
                            // Otherwise get user with latest login timestamp
                            const sortedByLastLogin =
                                product.productConfig.sort(
                                    (a, b) =>
                                        new Date(b.lastLoginTimeStamp || 0) -
                                        new Date(a.lastLoginTimeStamp || 0)
                                );
                            if (sortedByLastLogin.length > 0) {
                                defaults[product.productName] =
                                    sortedByLastLogin[0].username;
                            }
                        }
                    }
                });
                setDropdownValues(defaults);
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };

        const fetchUserInfo = async () => {
            try {
                let token = Cookies.get("_t");
                const headers = token
                    ? { Authorization: `bearer ${token}` }
                    : {};
                const response = await requestWithHeaders(
                    "/api/account/user-info",
                    "POST",
                    null,
                    headers
                );
                setUser(response.data);
            } catch (error) {
                console.error("Error fetching user info:", error);
            }
        };
        // refreshToken();
        fetchData();
        fetchUserInfo();
    }, []);

    const ref = useResizeObserver(() => setCurrentBreakpoint(getViewSize()));

    const [dropdownValues, setDropdownValues] = useState({});
    const [isSwitchingSession, setIsSwitchingSession] = useState(false);

    const handleDropdownChange = (productName, value) => {
        const normalizedValue =
            value && value.target ? value.target.value : value;
        setDropdownValues((prev) => ({
            ...prev,
            [productName]: normalizedValue,
        }));
    };

    const getResolvedTargetUrl = (product, selectedUserName) => {
        const productConfig = product?.productConfig || [];
        const selectedConfig = productConfig.find(
            (opt) => opt.username === selectedUserName
        );

        return selectedConfig?.targetUrl || product?.targetUrl;
    };

    const getResolvedTargetProduct = (product, selectedUserName) => {
        const productConfig = product?.productConfig || [];
        const selectedConfig = productConfig.find(
            (opt) => opt.username === selectedUserName
        );

        return selectedConfig?.targetProduct || product?.productName;
    };

    const getResponsiveStyles = () => {
        const baseStyles = {
            containerPadding: "10px 12px",
            containerMinHeight: 40,
            spanFontSize: 11,
            usernameMaxWidth: 280,
        };

        switch (currentBreakpoint) {
            case "xs":
                return {
                    containerPadding: "8px 10px",
                    containerMinHeight: 36,
                    spanFontSize: 10,
                    usernameMaxWidth: "100%",
                    selectMargin: "8px 0 0 0",
                };
            case "sm":
                return {
                    containerPadding: "9px 11px",
                    containerMinHeight: 38,
                    spanFontSize: 10.5,
                    usernameMaxWidth: 200,
                    selectMargin: "10px 0 0 0",
                };
            case "md":
                return {
                    containerPadding: "10px 12px",
                    containerMinHeight: 40,
                    spanFontSize: 11,
                    usernameMaxWidth: 230,
                    selectMargin: "12px 0 0 0",
                };
            default:
                // lg and xl
                return {
                    containerPadding: "10px 12px",
                    containerMinHeight: 40,
                    spanFontSize: 11,
                    usernameMaxWidth: 280,
                    selectMargin: "16px 0 0 0",
                };
        }
    };

    const handleProductNavigation = async (e, product) => {
        e.preventDefault();

        if (product.productName === "Research") {
            window.location.assign(product.targetUrl);
            return;
        }

        const selectedUserName = dropdownValues[product.productName];
        if (!selectedUserName) {
            showInfoNotification("Please select a user before continuing.");
            return;
        }

        const resolvedTargetUrl = getResolvedTargetUrl(
            product,
            selectedUserName
        );
        const resolvedTargetProduct = getResolvedTargetProduct(
            product,
            selectedUserName
        );

        if (!resolvedTargetUrl || isSwitchingSession) {
            return;
        }

        setIsSwitchingSession(true);
        try {
            if (user.username === selectedUserName) {
                window.location.assign(resolvedTargetUrl);
                return;
            }

            const response = await requestWithParams(
                "/api/authentication/update-session",
                "POST",
                {
                    username: selectedUserName,
                    product: resolvedTargetProduct,
                },
                null
            );

            if (response?.response?.ok) {
                localStorage.clear();
                sessionStorage.clear();
                window.location.assign(resolvedTargetUrl);
                return;
            } else {
                setIsSwitchingSession(false);

                showInfoNotification(
                    "Unable to update session. Please try again."
                );
            }
        } catch (error) {
            setIsSwitchingSession(false);
            showInfoNotification("Unable to update session. Please try again.");
        }
    };

    const loadingComponent = useCallback(
        () => (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Loading enabled={true} spinnerText="Loading..." />
            </div>
        ),
        []
    );

    return (
        <>
            {isSwitchingSession || !user ? (
                loadingComponent()
            ) : (
                <StylesProvider jss={jss}>
                    <MuiThemeProvider theme={theme}>
                        <ViewportContext.Provider value={{ currentBreakpoint }}>
                            <div id="main-view" ref={ref} className={classes}>
                                <Header
                                    userInfo={user}
                                    DarkMode={user?.darkMode}
                                />
                                <div className="content-wrapper">
                                    <div className="landing-page-wrapper">
                                        <div className="product-container">
                                            {products.map((product, idx) => {
                                                const productColor =
                                                    {
                                                        "Venue Statistics":
                                                            "rgb(249, 162, 21)",
                                                        "TCA": "rgb(54, 105, 211)",
                                                        "Surveillance":
                                                            "rgb(204, 63, 17)",
                                                        "Research":
                                                            "rgb(137, 57, 157)",
                                                    }[product.displayName] ||
                                                    "rgb(249, 162, 21)";
                                                const dropdownKey =
                                                    product.productName;
                                                const productConfig =
                                                    product.productConfig || [];
                                                const selectedUserName =
                                                    dropdownValues[dropdownKey];
                                                const resolvedTargetUrl =
                                                    getResolvedTargetUrl(
                                                        product,
                                                        selectedUserName
                                                    );
                                                const hasSingleConfig =
                                                    productConfig.length === 1;
                                                const emptyConfig =
                                                    productConfig.length === 0;
                                                const singleConfig =
                                                    productConfig[0] || {};
                                                const responsiveStyles =
                                                    getResponsiveStyles();
                                                return (
                                                    <div
                                                        className={`product ${
                                                            product.isUserSubscribed
                                                                ? "subscribed"
                                                                : ""
                                                        }`}
                                                        key={idx}
                                                    >
                                                        <a
                                                            href={
                                                                resolvedTargetUrl
                                                            }
                                                            onClick={(e) =>
                                                                handleProductNavigation(
                                                                    e,
                                                                    product
                                                                )
                                                            }
                                                        >
                                                            <div
                                                                className="image-container"
                                                                style={{
                                                                    borderTopColor:
                                                                        productColor,
                                                                }}
                                                            >
                                                                <img
                                                                    src={
                                                                        product?.productImagePath
                                                                    }
                                                                    alt={
                                                                        product?.displayName
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="product-info">
                                                                <h2 className="title">
                                                                    {
                                                                        product?.displayName
                                                                    }
                                                                </h2>
                                                                <div className="description">
                                                                    <ul>
                                                                        {product.productDescriptions?.map(
                                                                            (
                                                                                desc,
                                                                                i
                                                                            ) => (
                                                                                <li
                                                                                    key={
                                                                                        i
                                                                                    }
                                                                                >
                                                                                    <span>
                                                                                        {
                                                                                            desc?.description
                                                                                        }
                                                                                    </span>
                                                                                    {desc?.hasBadge && (
                                                                                        <div className="description-badge-container">
                                                                                            <span className="lm-badge lm-badge-s lm-badge-primary">
                                                                                                {
                                                                                                    desc?.badgeLabel
                                                                                                }
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </li>
                                                                            )
                                                                        )}
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                            {product.productName !==
                                                                "Research" && (
                                                                <div
                                                                    className="product-account-selection"
                                                                    style={{
                                                                        margin: 16,
                                                                        overflow:
                                                                            "visible",
                                                                        position:
                                                                            "relative",
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="product-username-single-container"
                                                                        style={{
                                                                            display:
                                                                                hasSingleConfig
                                                                                    ? "block"
                                                                                    : "none",
                                                                            borderRadius: 4,
                                                                            padding:
                                                                                responsiveStyles.containerPadding,
                                                                            minHeight:
                                                                                responsiveStyles.containerMinHeight,
                                                                        }}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "flex",
                                                                                flexDirection:
                                                                                    "column",
                                                                                lineHeight: 1.1,
                                                                                alignItems:
                                                                                    "flex-start",
                                                                            }}
                                                                        >
                                                                            <span
                                                                                className="product-username-single"
                                                                                style={{
                                                                                    maxWidth:
                                                                                        "100%",
                                                                                    overflow:
                                                                                        "hidden",
                                                                                    textOverflow:
                                                                                        "ellipsis",
                                                                                    whiteSpace:
                                                                                        "nowrap",
                                                                                    display:
                                                                                        "block",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    singleConfig.username
                                                                                }
                                                                            </span>
                                                                            <span
                                                                                style={{
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "flex-start",
                                                                                    justifyContent:
                                                                                        "space-between",
                                                                                    width: "100%",
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        fontSize:
                                                                                            responsiveStyles.spanFontSize,
                                                                                        color: "#888",
                                                                                        marginTop: 2,
                                                                                        paddingRight: 10,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        singleConfig.bic
                                                                                    }
                                                                                </span>
                                                                                {singleConfig.lastLoginTimeStamp && (
                                                                                    <span
                                                                                        style={{
                                                                                            fontSize:
                                                                                                responsiveStyles.spanFontSize,
                                                                                            color: "#888",
                                                                                            marginTop: 2,
                                                                                        }}
                                                                                    >
                                                                                        {" "}
                                                                                        Last
                                                                                        Used:{" "}
                                                                                        {moment(
                                                                                            singleConfig.lastLoginTimeStamp
                                                                                        ).isValid()
                                                                                            ? moment(
                                                                                                  singleConfig.lastLoginTimeStamp
                                                                                              ).format(
                                                                                                  "DD-MMM-YYYY"
                                                                                              )
                                                                                            : null}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <FormControl
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        style={{
                                                                            display:
                                                                                hasSingleConfig ||
                                                                                emptyConfig
                                                                                    ? "none"
                                                                                    : "block",
                                                                            marginTop:
                                                                                responsiveStyles.selectMargin,
                                                                        }}
                                                                    >
                                                                        <Select
                                                                            labelId={`dropdown-label-${product.productName}`}
                                                                            id={`dropdown-${product.productName}`}
                                                                            value={
                                                                                dropdownValues[
                                                                                    dropdownKey
                                                                                ] ||
                                                                                ""
                                                                            }
                                                                            label="Select Option"
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                handleDropdownChange(
                                                                                    dropdownKey,
                                                                                    e
                                                                                )
                                                                            }
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                e.stopPropagation()
                                                                            }
                                                                        >
                                                                            {productConfig.map(
                                                                                (
                                                                                    opt
                                                                                ) => (
                                                                                    <MenuItem
                                                                                        key={
                                                                                            opt.username
                                                                                        }
                                                                                        value={
                                                                                            opt.username
                                                                                        }
                                                                                    >
                                                                                        <div
                                                                                            style={{
                                                                                                display:
                                                                                                    "flex",
                                                                                                flexDirection:
                                                                                                    "column",
                                                                                                lineHeight: 1.1,
                                                                                                alignItems:
                                                                                                    "flex-start",
                                                                                            }}
                                                                                        >
                                                                                            <span
                                                                                                style={{
                                                                                                    maxWidth:
                                                                                                        responsiveStyles.usernameMaxWidth,
                                                                                                    minWidth:
                                                                                                        currentBreakpoint ===
                                                                                                        "xs"
                                                                                                            ? "100%"
                                                                                                            : 200,
                                                                                                    overflow:
                                                                                                        "hidden",
                                                                                                    textOverflow:
                                                                                                        "ellipsis",
                                                                                                    whiteSpace:
                                                                                                        "nowrap",
                                                                                                    display:
                                                                                                        "inline-block",
                                                                                                    verticalAlign:
                                                                                                        "bottom",
                                                                                                    textAlign:
                                                                                                        "left",
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    opt.username
                                                                                                }
                                                                                            </span>
                                                                                            <span
                                                                                                style={{
                                                                                                    display:
                                                                                                        "flex",
                                                                                                    alignItems:
                                                                                                        "flex-start",
                                                                                                    justifyContent:
                                                                                                        "space-between",
                                                                                                    width: "100%",
                                                                                                }}
                                                                                            >
                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            responsiveStyles.spanFontSize,
                                                                                                        color: "#888",
                                                                                                        marginTop: 2,
                                                                                                        paddingRight: 10,
                                                                                                    }}
                                                                                                >
                                                                                                    {
                                                                                                        opt.bic
                                                                                                    }
                                                                                                </span>
                                                                                                {opt.lastLoginTimeStamp && (
                                                                                                    <span
                                                                                                        style={{
                                                                                                            fontSize:
                                                                                                                responsiveStyles.spanFontSize,
                                                                                                            color: "#888",
                                                                                                            marginTop: 2,
                                                                                                        }}
                                                                                                    >
                                                                                                        {" "}
                                                                                                        Last
                                                                                                        Used:{" "}
                                                                                                        {moment(
                                                                                                            opt.lastLoginTimeStamp
                                                                                                        ).isValid()
                                                                                                            ? moment(
                                                                                                                  opt.lastLoginTimeStamp
                                                                                                              ).format(
                                                                                                                  "DD-MMM-YYYY"
                                                                                                              )
                                                                                                            : null}
                                                                                                    </span>
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    </MenuItem>
                                                                                )
                                                                            )}
                                                                        </Select>
                                                                    </FormControl>
                                                                </div>
                                                            )}
                                                        </a>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <Footer />
                            </div>
                        </ViewportContext.Provider>
                    </MuiThemeProvider>
                </StylesProvider>
            )}
        </>
    );
};

export default Landing;
